import moment from "moment-timezone";
import * as XLSX from "xlsx";
import {
  appendTimeMeasureColumns,
  getTimeMeasureDateKeys,
} from "../../utils/timeMeasureExport";

function datenum(v) {
  return (v - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000);
}

function fixTimezone(date) {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs);
}

// Zamienia wskazane kolumny arkusza (po nagłówku) z tekstowych dat ISO (UTC, tak jak
// JSON serializuje obiekty Date z backendu) na natywne komórki daty/godziny Excela.
function applyDateTimeFormatting(worksheet, dateKeys) {
  if (!worksheet["!ref"]) {
    return;
  }
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const header = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })]?.v;
    if (dateKeys.includes(header)) {
      for (let R = 1; R <= range.e.r; ++R) {
        const cell_ref = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cell_ref];
        if (cell && (typeof cell.v === "string" || cell.v instanceof Date)) {
          const parsedDate = new Date(cell.v);
          if (!isNaN(parsedDate)) {
            const localDate = fixTimezone(parsedDate);
            cell.v = datenum(localDate);
            cell.t = "n";
            cell.z = "yyyy-mm-dd hh:mm:ss";
          }
        }
      }
    }
  }
}

export function convertToXLSX(data, timeMeasureOptions = {}) {
  // Mapowanie nazw kolumn
  const columnMapping = {
    nrRef: "nr Ref",
    importer: "Importer",
    eksporter: "Eksporter",
    osobaWysylajacaOdprawe: "Os. wysyłająca",
    osobaPrzygotowujacaOdprawe: "Os. przygotowująca",
    osobaWeryfikujacaOdprawe: "Os. weryfikująca",
    statusRealizacji: "Status",
    rodzaj: "Rodzaj odprawy",
    odprawaNaKolachBox: "Znacznik Na Kołach",
    fakturaBox: "Znacznik Faktura",
    listPrzewozowyBox: "Znacznik List Przewozowy",
    kosztyTransportuBox: "Znacznik Koszty transportu",
    informacjeOTowarzeBox: "Znacznik Informacje o towarze",
    rewizja: "Rewizja",
    T1Box: "Znacznik T1",
    nrFaktury: "nr Faktury",
    kurierBox: "Znacznik Kurier",
    rodzajTransportu: "Rodzaj transportu",
    SAD: "SAD",
    nrRejestracyjnyPojazdyKurier: "nr Rejestracyjny pojazdu/Kuriera",
    lokalizacjaTowaru: "Lokalizacja towaru",
    id: "Id",
    data: "Data T1",
    swiadectwoBox: "Znacznik Świadectwo",
    MRN: "MRN",
    dokPrzedPrzybyciem: "dok. Przed przybyciem",
    plikiBox: "Znacznik Plik do zaczytania",
    sankcjeBox: "Znacznik Sankcje",
    CBAMBox: "Znacznik CBAM",
    CEBox: "Znacznik CE",
    wagaMailBox: "Znacznik waga mail",
    wagaTabelaBox: "Znacznik Waga tabela",
    wagaPropBox: "Znacznik waga Prop",
    wagaDokBox: "Znacznik Waga Dok",
    endDate: "Data zakończenia",
    adresatDokumentow: "Adresat dokumentów",
    kilkaRazyBox: "Znacznik kilka razy wysłane",
    wiecejNiz3Box:
      "Znacznik więcej niż 3 pozycje na fakturze wpisane ręcznie",
    osobaMonitorujacaOdprawe: "Os. monitorująca",
    odprawaPo16Box: "Znacznik odprawa po 16",
    zglosBladBox: "Znacznik zgłoś błąd",
    brakUpowaznieniaBox: "Znacznik brak upoważnienia",
    eoriBox: "Znacznik Eori",
    emailKorespondencyjny: "Email korespondencyjny",
    KWBox: "Znacznik KW (kontrola wewnętrzna)",
    pilneBox: "Znacznik Pilne",
    odprawaLaczonaBox: "Znacznik odprawa łączona",
    brakFakturyEdytowalnejBox: "Znacznik Brak faktury edytowalnej",
    brakPackingListEdytowalnegoBox: "Znacznik Brak Packing List edytowalnego",
    brakIncotermsBox: "Znacznik Brak Incoterms",
    oczekiwanieNaPlatnoscBox: "Znacznik oczekiwanie na płatność",
    ponownyMailOBrakiBox: "Znacznik ponowny mail o braki (Rejestr Wniosków)",
    dataMailOBrakiBox: "Data mail o braki",
    ATRBox: "Znacznik A. TR",
    EURBox: "Znacznik EUR. 1",
    T2LBox: "Znacznik T2L",
    RPSBox: "Znacznik RPS",
    WIJHARSBox: "Znacznik WIJHARS",
    SingleWindowBox: "Znacznik SingleWindow",
    ObecnoscPracownikaBox: "Znacznik Obecność pracownika przy kontroli",
    COOBox: "Znacznik COO",
    WysylkaDokumentowBox: "Znacznik Wysyłka dokumentów",
    SanepidBox: "Znacznik Sanepid",
    "utworzono data": "Utworzono data",
    "utworzono godzina": "Utworzono godzina",
  };

  // Modyfikujemy dane wejściowe, żeby rozbić "utworzono"
  const transformedData = data.map((row) => {
    const newRow = { ...row };
    appendTimeMeasureColumns(newRow, timeMeasureOptions);
    if (newRow.utworzono) {
      const dateObj = new Date(newRow.utworzono);
      if (!isNaN(dateObj)) {
        newRow["utworzono data"] = dateObj.toISOString().split("T")[0]; // "YYYY-MM-DD"
        newRow["utworzono godzina"] = dateObj.toTimeString().split(" ")[0]; // "HH:mm:ss"
        delete newRow.utworzono; // jeśli chcesz usunąć oryginalne pole
      }
    }

    // Mapowanie nazw kolumn
    const mappedRow = {};
    Object.keys(newRow).forEach((key) => {
      const newKey = columnMapping[key] || key;
      mappedRow[newKey] = newRow[key];
    });

    return mappedRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  const dateKeys = [
    "Data zakończenia",
    "Data T1",
    "data_ie",
    "Data mail o braki",
    ...getTimeMeasureDateKeys(timeMeasureOptions),
  ];
  applyDateTimeFormatting(worksheet, dateKeys);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Dane");
  return workbook;
}

export function generateFakturaReport(data) {
  const fakturaColumns = {
    importer: "Importer",
    eksporter: "Eksporter",
    rodzaj: "Rodzaj",
    statusRealizacji: "Status realizacji",
    nrRejestracyjnyPojazdyKurier: "Nr rejestracyjne",
    lokalizacjaTowaru: "Lokalizacja towaru",
    nrRef: "Nr ref",
    MRN: "MRN",
    emailKorespondencyjny: "Email korespondencyjny",
    "utworzono data": "Utworzono data",
    "utworzono godzina": "Utworzono godzina",
    "data odebrania dokumentow": "Data odebrania dokumentów",
    "godzina odebrania dokumentow": "Godzina odebrania dokumentów",
  };

  const transformedData = data.map((row) => {
    const newRow = {};

    if (row.utworzono) {
      const dateObj = new Date(row.utworzono);
      if (!isNaN(dateObj)) {
        newRow["utworzono data"] = dateObj.toISOString().split("T")[0];
        newRow["utworzono godzina"] = dateObj.toTimeString().split(" ")[0];
      }
    }

    if (row.dataOdebraniaDokumentow) {
      const pickupDate = moment.tz(
        row.dataOdebraniaDokumentow,
        ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm"],
        "Europe/Warsaw",
      );

      if (pickupDate.isValid()) {
        newRow["data odebrania dokumentow"] = pickupDate.format("YYYY-MM-DD");
        newRow["godzina odebrania dokumentow"] =
          pickupDate.format("HH:mm:ss");
      }
    }

    newRow.emailKorespondencyjny = row.emailKorespondencyjny ?? "";

    Object.keys(fakturaColumns).forEach((key) => {
      if (!(key in newRow)) {
        newRow[key] = row[key] ?? "";
      }
    });

    return newRow;
  });

  const displayData = transformedData.map((row) => {
    const mapped = {};
    Object.entries(fakturaColumns).forEach(([key, label]) => {
      mapped[label] = row[key] ?? "";
    });
    return mapped;
  });

  const worksheet = XLSX.utils.json_to_sheet(displayData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Zestawienie Fakturowe");

  // 📅 Dodajemy datę do nazwy pliku
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  const dateSuffix = `${dd}_${mm}_${yyyy}`;

  const fileName = `zestawienie_fakturowe_${dateSuffix}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}
